<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, reactive, ref, toRef, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { usePolledScan, type Scan } from '@/lib/usePolledScan'
import { useCatalogue } from '@/lib/catalogue'
import { groupFindings } from '@/lib/group'
import { GONE_VERDICT, isTruncated, verdictFor } from '@/lib/verdict'
import { CATEGORY_ORDER } from '@/lib/tone'
import VerdictPanel from '@/components/VerdictPanel.vue'
import SummaryPanel from '@/components/SummaryPanel.vue'
import IncompleteBanner from '@/components/IncompleteBanner.vue'
import CategoryNav from '@/components/CategoryNav.vue'
import CategorySection from '@/components/CategorySection.vue'
import ScanErrorList from '@/components/ScanErrorList.vue'
import ProgressStrip from '@/components/ProgressStrip.vue'
import ReportSkeleton from '@/components/ReportSkeleton.vue'
import ToneGlyph from '@/components/ToneGlyph.vue'

const props = defineProps<{ id: string }>()

const route = useRoute()
const router = useRouter()

const { scan, phase, error, isPolling, retry } = usePolledScan(toRef(props, 'id'))
const { describe } = useCatalogue()

/**
 * The target, before the first response arrives.
 *
 * The form pushes the canonical target into history state when it navigates
 * here, so pressing the button paints "example.com — Queued" on the next frame
 * instead of a skeleton with nothing in it. History state survives reload and
 * back/forward, which a route param would too but a store would not.
 *
 * It is an optimisation and nothing depends on it. A cold load of a shared link
 * has no history state and falls back to the skeleton until the first poll.
 */
const optimisticTarget = ref<string>(
  typeof history.state?.target === 'string' ? history.state.target : '',
)

/**
 * Whether the request that started this scan asked for a port sweep.
 *
 * Rides in history state next to the target, and for the same reason: the form
 * knows something about this scan that `Query.scan` does not report. Only the
 * fact is read, never the profile name, because the profile changes nothing on
 * this page and a value out of history state is not something to render.
 *
 * False for a cold load or a shared link, where this page did not make the
 * request. That is the honest answer rather than a gap: it holds no row open for
 * findings it cannot promise, and the note under the summary table is worded to
 * be true whether the sweep was skipped or merely unknown here.
 */
const sweepRequested = typeof history.state?.portProfile === 'string'

const target = computed(() => scan.value?.target ?? optimisticTarget.value)

const verdict = computed(() => {
  if (phase.value === 'missing') return GONE_VERDICT
  const current = scan.value
  if (current === null) return null
  return verdictFor({
    state: current.state,
    outcome: current.outcome,
    summary: current.summary,
    errorCount: current.errors.length,
  })
})

/**
 * Sections, rederived from the current findings array on every poll.
 *
 * `groupFindings` makes one left-to-right pass and never sorts. The schema says
 * the order it sends is meaningful and reproducible and asks a client to render
 * it as it arrives, and while a scan is running the server keeps the whole list
 * in canonical order, so a finding can be inserted ahead of ones already on
 * screen rather than only appended. Rebuilding beats patching here: it is a few
 * microseconds for a few hundred findings and it cannot drift.
 */
interface CategoryCounts {
  total: number
  passed: number
  failed: number
  unknown: number
}

/**
 * The rows `SummaryPanel` holds open while the scan is still running.
 *
 * Seven of the eight categories run on every scan, so reserving their rows from
 * the first frame costs nothing and stops the table growing under the reader. The
 * prop's own comment carries the measurement. PORT is the exception. The scanner
 * sweeps ports only when the caller asked for it and confirmed authorization, so
 * most scans never produce a port finding, and a reserved row that sits empty
 * for the whole scan and then vanishes when the table switches to what actually
 * ran is worse than a row that arrives late.
 *
 * So PORT is reserved on either of two signals. The first is the request itself:
 * when the form navigated here it said whether it asked for a sweep, and a scan
 * that asked has port findings coming, so the row is held open from the first
 * frame like the other seven. The second is the scan reporting a port finding
 * anyway, which covers every page this one did not open — a shared link, a
 * reload — and cannot come too late to matter, because `byCategory` only ever
 * grows, so once the row is in it stays in.
 *
 * `Scan` still says what a scan has found so far and never what it set out to
 * look for, so the first signal is only as good as the history state carrying it.
 * A field on `Scan` naming the categories this scan will report would make the
 * reserved set exact for every reader rather than for the one who pressed the
 * button, and would keep it exact the day the scanner grows a ninth.
 */
function reservedRows(current: Scan): readonly string[] | undefined {
  if (current.state === 'FINISHED') return undefined
  if (sweepRequested) return CATEGORY_ORDER
  if (current.summary.byCategory.some((entry) => entry.category === 'PORT')) return CATEGORY_ORDER
  return CATEGORY_ORDER.filter((category) => category !== 'PORT')
}

/**
 * The derived views of the report, built together from one read of `scan`.
 *
 * Kept in one computed rather than three so there is a single place that deals
 * with the scan not being loaded yet. Separate ones each needed their own null
 * fallback, and all but the first was a branch no render could reach because the
 * template only asks for them once the scan exists.
 */
const report = computed(() => {
  const current = scan.value
  if (current === null) {
    return { sections: [], counts: new Map<string, CategoryCounts>(), reserved: undefined }
  }
  return {
    sections: groupFindings(current.findings),
    counts: new Map<string, CategoryCounts>(
      current.summary.byCategory.map((entry) => [
        entry.category as string,
        {
          total: entry.total,
          passed: entry.passed,
          failed: entry.failed,
          unknown: entry.unknown,
        },
      ]),
    ),
    reserved: reservedRows(current),
  }
})

const sections = computed(() => report.value.sections)
const categorySummary = computed(() => report.value.counts)
const reservedCategories = computed(() => report.value.reserved)

const navSections = computed(() =>
  sections.value.map((section) => {
    const authoritative = categorySummary.value.get(section.category)
    return {
      category: section.category,
      anchor: section.anchor,
      failed: authoritative?.failed ?? section.rendered.failed,
      unknown: authoritative?.unknown ?? section.rendered.unknown,
      total: authoritative?.total ?? section.rendered.total,
    }
  }),
)

/* --------------------------------------------------------------------------
   Passing checks: collapsed by default, and the master switch is in the URL

   Roughly four findings in five pass. Showing them all by default buries the
   handful that matter, and hiding them with no way back would be worse, so each
   run collapses behind a count and the whole lot can be opened at once. That
   master switch goes in the query string because "here is the report with
   everything shown" is a thing somebody sends to a colleague.

   Individual toggles stay out of the URL. They are a reading aid, not a view
   worth addressing, and writing one per click would fill the history.
   -------------------------------------------------------------------------- */

const showAllPasses = computed(() => route.query.passes === 'shown')
const openRuns = reactive(new Set<string>())

function isRunOpen(runId: string): boolean {
  return showAllPasses.value || openRuns.has(runId)
}

function setRunOpen(runId: string, open: boolean) {
  if (open) openRuns.add(runId)
  else openRuns.delete(runId)
}

function toggleAllPasses() {
  const next = !showAllPasses.value
  openRuns.clear()
  void router.replace({
    query: { ...route.query, passes: next ? 'shown' : undefined },
    hash: route.hash,
  })
}

/* --------------------------------------------------------------------------
   Where am I in the report
   -------------------------------------------------------------------------- */

const activeAnchor = ref('')
let observer: IntersectionObserver | null = null

function observeSections() {
  observer?.disconnect()
  if (typeof IntersectionObserver === 'undefined') return

  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          activeAnchor.value = entry.target.id
          break
        }
      }
    },
    // A band near the top of the viewport. A section counts as "where you are"
    // when its heading is under the sticky header, not when any part of it is
    // visible, which on a long report would be three sections at once.
    { rootMargin: '-88px 0px -70% 0px', threshold: 0 },
  )

  for (const section of sections.value) {
    const element = document.getElementById(section.anchor)
    if (element) observer.observe(element)
  }
}

/**
 * A fragment in the URL of a page whose content arrives later.
 *
 * `/scan/<id>#category-tls` is a link somebody sends to point at one part of a
 * report. On a cold load the router tries to scroll to it while the page is
 * still a skeleton, finds nothing, and gives up, so the link silently lands at
 * the top. Worse on a live scan, where the section may be twenty seconds away.
 *
 * So the fragment is held and applied the first time its section actually
 * exists, and then forgotten. Once, deliberately: if the reader has started
 * scrolling by the time a late section appears, yanking them somewhere else
 * would be the wrong end of helpful.
 */
let pendingHash = route.hash.slice(1)

function applyPendingHash() {
  if (!pendingHash) return
  const element = document.getElementById(pendingHash)
  if (!element) return
  pendingHash = ''
  // One call, and no `behavior` argument. Every section carries `scroll-mt-24`,
  // so the sticky header offset is already in the CSS and does not need a
  // second relative scroll on top, which would fight this one and lose.
  // Leaving `behavior` unset lets the `scroll-behavior` rule in main.css decide,
  // and that rule is already switched off under prefers-reduced-motion.
  element.scrollIntoView({ block: 'start' })
  activeAnchor.value = element.id
}

watch(
  () => sections.value.map((section) => section.anchor).join(','),
  async () => {
    await nextTick()
    observeSections()
    applyPendingHash()
  },
  { immediate: true },
)

// A reader who scrolls has taken over. Stop trying to move them.
function abandonPendingHash() {
  pendingHash = ''
}

window.addEventListener('wheel', abandonPendingHash, { once: true, passive: true })
window.addEventListener('touchmove', abandonPendingHash, { once: true, passive: true })
window.addEventListener('keydown', abandonPendingHash, { once: true })

onBeforeUnmount(() => {
  observer?.disconnect()
  window.removeEventListener('wheel', abandonPendingHash)
  window.removeEventListener('touchmove', abandonPendingHash)
  window.removeEventListener('keydown', abandonPendingHash)
})

function jumpTo(anchor: string) {
  // Through the router rather than `scrollIntoView`, so the fragment lands in
  // the URL and the back button undoes the jump. `scrollBehavior` in router.ts
  // applies the sticky-header offset, and the smooth-scroll rule in main.css is
  // already switched off under prefers-reduced-motion.
  void router.push({ hash: `#${anchor}`, query: route.query })
}
</script>

<template>
  <div class="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6 sm:py-10">
    <!-- Live scans only. Sticks under the header so progress stays visible
         while the reader is already scrolling through what has arrived. -->
    <div
      v-if="scan && scan.state !== 'FINISHED'"
      class="sticky top-[57px] z-20 -mx-4 mb-6 bg-canvas/90 px-4 pt-2 pb-3 backdrop-blur-sm sm:-mx-6 sm:px-6"
    >
      <ProgressStrip
        :state="scan.state"
        :reported="scan.summary.total"
        :is-polling="isPolling"
      />
    </div>

    <ReportSkeleton v-if="phase === 'loading'" />

    <template v-else>
      <!-- A dropped poll is a blip, not a reason to replace a half-drawn
           report. This only appears after four consecutive failures, and the
           findings already on screen stay where they are because they are
           still true. -->
      <Transition name="panel">
        <div
          v-if="error && scan"
          role="status"
          class="card mb-6 flex flex-wrap items-center gap-3 border-line-strong p-4"
        >
          <ToneGlyph
            glyph="question"
            :size="16"
          />
          <p class="text-sm text-ink">
            Lost contact with the scanner. The report below is what arrived before that.
          </p>
          <button
            type="button"
            class="btn-secondary ml-auto text-sm"
            @click="retry"
          >
            Try again
          </button>
        </div>
      </Transition>

      <VerdictPanel
        v-if="verdict"
        :verdict="verdict"
        :target="target"
        :scanned-at="scan?.startedAt"
        :finished-at="scan?.finishedAt"
        class="mb-6"
      />

      <!-- Nothing to read. Offer the way out rather than an empty page. -->
      <div
        v-if="phase === 'missing' || phase === 'error'"
        class="card p-6"
      >
        <p
          v-if="phase === 'error'"
          class="mb-4 text-ink-muted"
        >
          {{ error?.userMessage }}
        </p>
        <div class="flex flex-wrap gap-3">
          <button
            v-if="phase === 'error'"
            type="button"
            class="btn-primary"
            @click="retry"
          >
            Try again
          </button>
          <RouterLink
            to="/"
            class="btn-secondary"
          >
            Run a new scan
          </RouterLink>
        </div>
      </div>

      <template v-else-if="scan">
        <IncompleteBanner
          v-if="isTruncated(scan.state, scan.outcome)"
          :outcome="scan.outcome"
          class="mb-6"
        />

        <SummaryPanel
          :summary="scan.summary"
          :error-count="scan.errors.length"
          :rendered-count="scan.findings.length"
          :reserve-categories="reservedCategories"
          class="mb-8"
          @jump="jumpTo"
        />

        <div class="lg:flex lg:items-start lg:gap-8">
          <!-- Where am I, where can I go, how do I get back. The rail answers
               the first two, and the reading control sits with them because
               "how do I get through this report" is the same question. -->
          <aside
            v-if="sections.length > 0"
            class="mb-8 lg:sticky lg:top-24 lg:mb-0 lg:w-60 lg:shrink-0"
          >
            <div class="card p-2 lg:p-3">
              <p class="eyebrow mb-1 px-2 lg:mb-2">
                Sections
              </p>
              <CategoryNav
                :sections="navSections"
                :active-anchor="activeAnchor"
                @jump="jumpTo"
              />
            </div>

            <button
              type="button"
              class="btn-secondary mt-3 w-full text-sm"
              :aria-pressed="showAllPasses"
              @click="toggleAllPasses"
            >
              {{ showAllPasses ? 'Collapse passing checks' : 'Show every passing check' }}
            </button>
          </aside>

          <div class="min-w-0 flex-1">
            <div class="space-y-10">
              <CategorySection
                v-for="section in sections"
                :key="section.anchor"
                :section="section"
                :summary="categorySummary.get(section.category) ?? null"
                :describe="describe"
                :open-passes="section.rows.some((row) => row.kind === 'passes' && isRunOpen(row.id))"
                @update:open-passes="
                  (open: boolean) => {
                    for (const row of section.rows) {
                      if (row.kind === 'passes') setRunOpen(row.id, open)
                    }
                  }
                "
              />

              <ScanErrorList
                :errors="scan.errors"
                :describe="describe"
              />

              <!-- A finished scan that produced nothing at all. Rare, and the
                   honest reading is that something went wrong rather than that
                   the target is perfect. -->
              <div
                v-if="scan.state === 'FINISHED' && sections.length === 0 && scan.errors.length === 0"
                class="card p-6"
              >
                <h2 class="mb-2 text-lg font-semibold">
                  This scan produced no findings
                </h2>
                <p class="text-ink-muted">
                  Not one of the ninety-two checks reported a result. That is not a clean bill of
                  health, it means the scan did not get far enough to test anything. Running it
                  again is worth a try.
                </p>
              </div>
            </div>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>
